import React, { useState, useRef, useEffect } from 'react';
import styles from './StrategyForm.module.css';

// Strategy "Mode" options. Value = what is stored/sent to the backend
// (must match VALID_MODES there); label = what the admin sees.
export const MODE_OPTIONS = [
  { value: 'Signal', label: 'Signal' },
  { value: 'Notification', label: 'Group Notification' },
  { value: 'NotifyMe', label: 'Notify me' },
  { value: 'Trade', label: 'Trade' },
  { value: 'CreateRecommendation', label: 'Create Recommendation' },
];

// The two notification targets are mutually exclusive: a signal is either sent
// to admin-selected groups OR only to the strategy's creator — never both (that
// would be ambiguous about who is notified). At most one may be selected; the
// other is greyed out while one is active.
export const NOTIFY_TARGET_MODES = ['Notification', 'NotifyMe'];

// Sub-options shown under "Create Recommendation": who receives the draft.
// 'notifyMe' = the strategy creator only; 'recipients' = chosen notification
// groups. Mirrors the backend, which decides the audience purely from whether
// any recommendation groups are selected.
export const RECO_TARGET_OPTIONS = [
  { key: 'notifyMe', label: 'Notify me' },
  { key: 'recipients', label: 'Select groups' },
];

/**
 * Multi-select dropdown for the strategy Mode. Lets the admin combine modes
 * (e.g. Signal + Trade, or Notify me + Trade) while enforcing the one rule that
 * Group Notification and Notify me can't both be on. Self-contained: own
 * open/close + click-outside, fixed-width trigger that truncates instead of
 * widening the row.
 */
const ModeMultiSelect = ({ selected = [], onChange, recoTarget = 'notifyMe', onPickRecoTarget }) => {
  const [open, setOpen] = useState(false);
  // Whether the "Create Recommendation" sub-options (Notify me / Select groups)
  // are revealed by hover. They also stay visible whenever the mode is selected,
  // so the current choice is always shown.
  const [recoHover, setRecoHover] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDocClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  // A notification target is disabled while the OTHER one is selected.
  const isDisabled = (value) => {
    if (!NOTIFY_TARGET_MODES.includes(value)) return false;
    const other = NOTIFY_TARGET_MODES.find((m) => m !== value);
    return selected.includes(other);
  };

  const toggle = (value) => {
    if (isDisabled(value)) return;
    const set = new Set(selected);
    if (set.has(value)) set.delete(value);
    else set.add(value);
    onChange([...set]);
  };

  const label =
    selected.length === 0
      ? 'Select mode'
      : selected
          .map((v) => (MODE_OPTIONS.find((o) => o.value === v) || {}).label || v)
          .join(', ');

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        className={styles.select}
        style={{
          textAlign: 'left',
          width: '100%',
          // Stable width: long combined labels truncate instead of widening.
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
        }}
        onClick={() => setOpen((o) => !o)}
      >
        {label}
      </button>

      {open && (
        <div
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 4px)',
            left: 0,
            // Wider than the 180px trigger so every option (e.g. "Create
            // Recommendation") fits on one line instead of wrapping.
            minWidth: 240,
            width: 'max-content',
            maxWidth: 320,
            background: 'var(--background-white)',
            border: '1px solid var(--border-light)',
            borderRadius: 'var(--radius-md)',
            padding: 'var(--spacing-sm)',
            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
          }}
        >
          {MODE_OPTIONS.map((o) => {
            const disabled = isDisabled(o.value);

            // "Create Recommendation" carries a sub-menu (Notify me / Select
            // groups) that picks the draft audience. The sub-options reveal on
            // hover and stay visible while the mode is selected; clicking one
            // also checks the parent (handled by onPickRecoTarget upstream).
            if (o.value === 'CreateRecommendation') {
              const isOn = selected.includes(o.value);
              return (
                <div
                  key={o.value}
                  style={{ position: 'relative' }}
                  onMouseEnter={() => setRecoHover(true)}
                  onMouseLeave={() => setRecoHover(false)}
                >
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 4px',
                      cursor: 'pointer',
                      textTransform: 'none',
                      letterSpacing: 'normal',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={isOn}
                      onChange={() => toggle(o.value)}
                    />
                    <span>{o.label}</span>
                  </label>

                  {/* Flyout sub-menu — a SEPARATE panel that opens to the LEFT of
                      this row on hover (not an inline expansion, so the main panel
                      never grows taller). Always mounted; we fade opacity +
                      visibility so it opens AND closes smoothly. The wrapper's
                      paddingRight bridges the visual gap to the panel so moving
                      the cursor across it doesn't drop the hover (no flicker). */}
                  <div
                    style={{
                      position: 'absolute',
                      right: '100%',
                      top: 0,
                      paddingRight: 8,
                      zIndex: 30,
                      opacity: recoHover ? 1 : 0,
                      visibility: recoHover ? 'visible' : 'hidden',
                      transform: recoHover ? 'translateX(0)' : 'translateX(6px)',
                      transition: 'opacity 0.18s ease, transform 0.18s ease, visibility 0.18s ease',
                      pointerEvents: recoHover ? 'auto' : 'none',
                    }}
                  >
                    <div
                      style={{
                        minWidth: 170,
                        background: 'var(--background-white)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--radius-md)',
                        padding: 'var(--spacing-sm)',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                      }}
                    >
                      {RECO_TARGET_OPTIONS.map((s) => (
                        <label
                          key={s.key}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            padding: '6px 4px',
                            cursor: 'pointer',
                            textTransform: 'none',
                            letterSpacing: 'normal',
                            whiteSpace: 'nowrap',
                          }}
                        >
                          <input
                            type="radio"
                            name="recoTarget"
                            checked={isOn && recoTarget === s.key}
                            onChange={() => onPickRecoTarget && onPickRecoTarget(s.key)}
                          />
                          <span>{s.label}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              );
            }

            return (
              <label
                key={o.value}
                title={disabled ? 'Group Notification and Notify me cannot both be selected.' : undefined}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  padding: '6px 4px',
                  cursor: disabled ? 'not-allowed' : 'pointer',
                  opacity: disabled ? 0.45 : 1,
                  // Reset the global `label {}` uppercase + letter-spacing that
                  // leaks in from the non-module form CSS, and keep on one line.
                  textTransform: 'none',
                  letterSpacing: 'normal',
                  whiteSpace: 'nowrap',
                }}
              >
                <input
                  type="checkbox"
                  checked={selected.includes(o.value)}
                  disabled={disabled}
                  onChange={() => toggle(o.value)}
                />
                <span>{o.label}</span>
              </label>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ModeMultiSelect;
